import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useState } from 'react';
import Dashboard from './components/Dashboard';
import AdminUpload from './components/AdminUpload';
import LoginPage from './pages/LoginPage';
import SuperAdminPage from './pages/SuperAdminPage';
import AdminPage from './pages/AdminPage';
import HealthDashboard from './pages/HealthDashboard';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './hooks/useAuth';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import { useHeartbeat } from './hooks/useHeartbeat';
import IdleTimeoutModal from './components/IdleTimeoutModal';
import CookieConsent from './components/CookieConsent';
import ErrorBoundary from './components/ErrorBoundary';
import { usePageTracking } from './hooks/usePageTracking';
import './App.css';

/**
 * App Content - Handles routing and idle timeout
 * Must be inside BrowserRouter to access useLocation
 */
const AppContent = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const location = useLocation();
  const [showIdleModal, setShowIdleModal] = useState(false);

  // Track SPA route changes in Adobe Analytics.
  usePageTracking();

  // Heartbeat: pings backend every 15s so the Users Over Time graph is accurate
  useHeartbeat();

  // Idle timeout (15 minutes)
  useIdleTimeout(15, () => {
    // Clean up resources on timeout
    performResourceCleanup();
    setShowIdleModal(true);
  });

  // Resource cleanup function
  const performResourceCleanup = () => {
    // Clear session storage (except auth tokens which have their own expiry)
    const authToken = sessionStorage.getItem('authToken');
    sessionStorage.clear();
    if (authToken) {
      sessionStorage.setItem('authToken', authToken);
    }

    // Clear any cached API responses
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }

    console.log('[Idle Timeout] Resources cleaned up');
  };

  if (authLoading) {
    return (
      <div className="app-loading-screen" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontFamily: 'Segoe UI, sans-serif',
        color: '#00646E'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #E0E0E0',
          borderTopColor: '#00646E',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '1rem'
        }}></div>
        <p style={{ fontSize: '1.1rem', fontWeight: 500 }}>Connecting to Siemens Microsoft Entra ID...</p>
      </div>
    );
  }

  if (!isAuthenticated && location.pathname !== '/login') {
    return <LoginPage />;
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/ops-sw" element={<Dashboard initialSegment="OPS-SW" />} />
        <Route path="/grow-plm" element={<Dashboard initialSegment="GROW_PLM" />} />
        <Route path="/lcs" element={<Dashboard initialSegment="LCS" />} />
        <Route path="/me" element={<Dashboard initialSegment="ME" />} />
        <Route path="/pes" element={<Dashboard initialSegment="PES" />} />
        <Route path="/sim" element={<Dashboard initialSegment="SIM" />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/super-admin" element={<SuperAdminPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/legacy" element={<AdminUpload />} />
        <Route path="/health" element={<ErrorBoundary><HealthDashboard /></ErrorBoundary>} />
      </Routes>
      
      {/* Show idle timeout modal for anonymous users */}
      {showIdleModal && (
        <IdleTimeoutModal onClose={() => setShowIdleModal(false)} />
      )}

      {/* Cookie consent banner */}
      <CookieConsent />
    </>
  );
};

/**
 * App Component
 * Root component with routing and authentication
 */
const App = () => {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;
